import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { settings } from "../../config/settings";
import { authAPI } from "../../services/api";
import {
  DEFAULT_SITE_STYLE,
  isValidSiteStyle,
} from "../../theme/themeConfig";

const getStoredSiteStyle = () => {
  try {
    const stored = localStorage.getItem(settings.STORAGE_KEYS.THEME);
    return isValidSiteStyle(stored) ? stored : DEFAULT_SITE_STYLE;
  } catch {
    return DEFAULT_SITE_STYLE;
  }
};

const persistSiteStyle = (siteStyle) => {
  localStorage.setItem(settings.STORAGE_KEYS.THEME, siteStyle);
};

export const saveSiteStyle = createAsyncThunk(
  "theme/saveSiteStyle",
  async (siteStyle, { getState, rejectWithValue }) => {
    const normalized = isValidSiteStyle(siteStyle) ? siteStyle : DEFAULT_SITE_STYLE;
    const { auth } = getState();

    if (auth.isAuthenticated) {
      try {
        await authAPI.updateSiteStyle(normalized);
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }

    return normalized;
  }
);

const initialState = {
  siteStyle: getStoredSiteStyle(),
  isSaving: false,
  error: null,
};

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setSiteStyle: (state, action) => {
      const normalized = isValidSiteStyle(action.payload)
        ? action.payload
        : DEFAULT_SITE_STYLE;
      state.siteStyle = normalized;
      persistSiteStyle(normalized);
    },
    applyUserSiteStyle: (state, action) => {
      const userStyle = action.payload;
      if (isValidSiteStyle(userStyle)) {
        state.siteStyle = userStyle;
        persistSiteStyle(userStyle);
      }
    },
    clearThemeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveSiteStyle.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveSiteStyle.fulfilled, (state, action) => {
        state.isSaving = false;
        state.siteStyle = action.payload;
        persistSiteStyle(action.payload);
      })
      .addCase(saveSiteStyle.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export const { setSiteStyle, applyUserSiteStyle, clearThemeError } =
  themeSlice.actions;
export default themeSlice.reducer;
